import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaBookOpen,
  FaCheckCircle,
  FaChevronDown,
  FaChevronUp,
  FaClock,
  FaExclamationTriangle,
  FaLayerGroup,
} from "react-icons/fa";
import { Spinner } from "src/components/shared/atoms/spinner";
import { useGetUserByIdQuery } from "../services/user.Service";
import { learnerTrackRepository } from "../repositories/learner-track.repository";
import {
  LearnerTopicsProgressAppModel,
  LearnerTrackAppModel,
  LearningTrackDetails,
} from "../types/learner-track.entity";

type LearnerTrackWithProgress = {
  track: LearnerTrackAppModel;
  progress: LearnerTopicsProgressAppModel[];
  trackDetails: LearningTrackDetails | null;
};

type OrganizedChapter = {
  chapter: LearningTrackDetails["chapters"][number];
  topics: {
    topic: LearningTrackDetails["chapters"][number]["topics"][number];
    progress: LearnerTopicsProgressAppModel | null;
  }[];
  stats: {
    total: number;
    completed: number;
    percentage: number;
    timeSpent: number;
  };
};

type OrganizedLevel = {
  level: string;
  chapters: OrganizedChapter[];
  stats: {
    total: number;
    completed: number;
    percentage: number;
    timeSpent: number;
  };
};

type CurrentFocus = {
  level: string;
  chapterId: string;
  chapterTitle: string;
  topicId: string;
  topicTitle: string;
  currentComponentOrder: number;
  spentTimeInMinutes: number;
  status: "IN_PROGRESS" | "SKIPPED" | "COMPLETED";
} | null;

const formatDate = (value?: { toDate?: () => Date } | null) => {
  if (!value?.toDate) return "N/A";
  return value.toDate().toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatMinutes = (minutes: number) => {
  if (!minutes) return "0 min";
  if (minutes < 60) return `${minutes} min`;

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
};

const levelOrder = ["A1", "A2", "B1", "B2", "C1", "C2"];

const getTopicStatus = (progress: LearnerTopicsProgressAppModel | null) =>
  progress?.status ?? "IN_PROGRESS";

const getTopicTimeSpent = (progress: LearnerTopicsProgressAppModel | null) => {
  if (!progress) return 0;

  if (progress.componentsProgress?.length) {
    return progress.componentsProgress.reduce(
      (sum, component) => sum + (component.spentTimeInMinutes || 0),
      0
    );
  }

  return progress.spentTimeInMinutes || 0;
};

const getCurrentComponentOrder = (
  progress: LearnerTopicsProgressAppModel | null
) => {
  if (!progress) return 0;

  if (progress.componentsProgress?.length) {
    return progress.componentsProgress.reduce(
      (max, component) => Math.max(max, component.componentOrder || 0),
      0
    );
  }

  return progress.currentComponentOrder || 0;
};

export const UserLearnerProgress: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { data: user, isLoading: userLoading, isError } = useGetUserByIdQuery(userId || "");
  const [isLoading, setIsLoading] = useState(true);
  const [tracks, setTracks] = useState<LearnerTrackWithProgress[]>([]);
  const [showIncompatible, setShowIncompatible] = useState<Record<string, boolean>>({});
  const [expandedTrackIds, setExpandedTrackIds] = useState<Record<string, boolean>>({});
  const hasLearnerRole = !!user?.roles?.some((role) => role.toLowerCase().includes("learner"));

  useEffect(() => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    const loadProgress = async () => {
      try {
        setIsLoading(true);
        const learnerTracks = await learnerTrackRepository.getLearnerTracksByLearnerId(userId);
        const withProgress = await Promise.all(
          learnerTracks.map(async (track) => {
            const [progress, trackDetails] = await Promise.all([
              learnerTrackRepository.getLearnerTopicsProgress(track.docRef),
              learnerTrackRepository.getLearningTrackById(track.trackId),
            ]);

            return {
              track,
              progress,
              trackDetails,
            };
          })
        );

        const sortedTracks = [...withProgress].sort((a, b) => {
          const aHasProgress = a.progress.length > 0 ? 1 : 0;
          const bHasProgress = b.progress.length > 0 ? 1 : 0;

          if (aHasProgress !== bHasProgress) {
            return bHasProgress - aHasProgress;
          }

          return b.track.assignedAt.toMillis() - a.track.assignedAt.toMillis();
        });

        setTracks(sortedTracks);
      } catch (error) {
        console.error("Failed to load learner progress", error);
      } finally {
        setIsLoading(false);
      }
    };

    void loadProgress();
  }, [userId]);

  const calculateStats = (progress: LearnerTopicsProgressAppModel[], trackDetails: LearningTrackDetails | null) => {
    if (!trackDetails) {
      return { completed: 0, total: 0, percentage: 0, timeSpent: 0 };
    }

    const total = trackDetails.chapters.reduce((sum, chapter) => sum + (chapter.topics?.length || 0), 0);
    const latestByTopic = new Map<string, LearnerTopicsProgressAppModel>();

    progress.forEach((item) => {
      const existing = latestByTopic.get(item.topicId);
      if (!existing || item.updatedAt.toMillis() > existing.updatedAt.toMillis()) {
        latestByTopic.set(item.topicId, item);
      }
    });

    const deduped = Array.from(latestByTopic.values());
    const completed = deduped.filter((item) => item.status === "COMPLETED").length;
    const timeSpent = deduped.reduce((sum, item) => sum + getTopicTimeSpent(item), 0);

    return {
      completed,
      total,
      percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
      timeSpent: Math.round(timeSpent),
    };
  };

  const organizeByLevel = (progress: LearnerTopicsProgressAppModel[], trackDetails: LearningTrackDetails | null): OrganizedLevel[] => {
    if (!trackDetails) {
      return [];
    }

    const latestByTopic = new Map<string, LearnerTopicsProgressAppModel>();
    progress.forEach((item) => {
      const existing = latestByTopic.get(item.topicId);
      if (!existing || item.updatedAt.toMillis() > existing.updatedAt.toMillis()) {
        latestByTopic.set(item.topicId, item);
      }
    });

    const grouped = new Map<string, LearningTrackDetails["chapters"]>();
    trackDetails.chapters.forEach((chapter) => {
      const level = chapter.langLevel || "Unspecified";
      if (!grouped.has(level)) {
        grouped.set(level, []);
      }
      grouped.get(level)?.push(chapter);
    });

    return Array.from(grouped.entries())
      .sort((a, b) => levelOrder.indexOf(a[0]) - levelOrder.indexOf(b[0]))
      .map(([level, chapters]) => {
        const organizedChapters = chapters
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((chapter) => {
            const topics = (chapter.topics || []).map((topic) => ({
              topic,
              progress: latestByTopic.get(topic.id) || null,
            }));

            const total = topics.length;
            const completed = topics.filter((topicItem) => getTopicStatus(topicItem.progress) === "COMPLETED").length;
            const timeSpent = topics.reduce((sum, topicItem) => sum + getTopicTimeSpent(topicItem.progress), 0);

            return {
              chapter,
              topics,
              stats: {
                total,
                completed,
                percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
                timeSpent: Math.round(timeSpent),
              },
            };
          });

        const total = organizedChapters.reduce((sum, chapter) => sum + chapter.stats.total, 0);
        const completed = organizedChapters.reduce((sum, chapter) => sum + chapter.stats.completed, 0);
        const timeSpent = organizedChapters.reduce((sum, chapter) => sum + chapter.stats.timeSpent, 0);

        return {
          level,
          chapters: organizedChapters,
          stats: {
            total,
            completed,
            percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
            timeSpent: Math.round(timeSpent),
          },
        };
      });
  };

  const getCurrentFocus = (
    progress: LearnerTopicsProgressAppModel[],
    trackDetails: LearningTrackDetails | null
  ): CurrentFocus => {
    if (!trackDetails) {
      return null;
    }

    const chaptersById = new Map(trackDetails.chapters.map((chapter) => [chapter.id, chapter]));
    const topicsById = new Map<string, { topic: LearningTrackDetails["chapters"][number]["topics"][number]; chapter: LearningTrackDetails["chapters"][number] }>();

    trackDetails.chapters.forEach((chapter) => {
      chapter.topics.forEach((topic) => {
        topicsById.set(topic.id, { topic, chapter });
      });
    });

    const inProgressItems = progress
      .filter((item) => {
        const chapter = chaptersById.get(item.chapterId);
        return !!chapter && topicsById.has(item.topicId) && item.status === "IN_PROGRESS";
      })
      .sort((a, b) => b.updatedAt.toMillis() - a.updatedAt.toMillis());

    const activeItem = inProgressItems[0];
    if (activeItem) {
      const current = topicsById.get(activeItem.topicId);
      if (!current) return null;

      return {
        level: current.chapter.langLevel || "Unspecified",
        chapterId: current.chapter.id,
        chapterTitle: current.chapter.title,
        topicId: current.topic.id,
        topicTitle: current.topic.title,
        currentComponentOrder: getCurrentComponentOrder(activeItem),
        spentTimeInMinutes: getTopicTimeSpent(activeItem),
        status: activeItem.status,
      };
    }

    const completedItems = progress
      .filter((item) => {
        const chapter = chaptersById.get(item.chapterId);
        return !!chapter && topicsById.has(item.topicId);
      })
      .sort((a, b) => b.updatedAt.toMillis() - a.updatedAt.toMillis());

    const latestCompleted = completedItems[0];
    if (!latestCompleted) {
      return null;
    }

    const current = topicsById.get(latestCompleted.topicId);
    if (!current) {
      return null;
    }

    return {
      level: current.chapter.langLevel || "Unspecified",
      chapterId: current.chapter.id,
      chapterTitle: current.chapter.title,
      topicId: current.topic.id,
      topicTitle: current.topic.title,
      currentComponentOrder: getCurrentComponentOrder(latestCompleted),
      spentTimeInMinutes: getTopicTimeSpent(latestCompleted),
      status: latestCompleted.status,
    };
  };

  const processedTracks = useMemo(
    () =>
      tracks.map((trackItem) => {
        const stats = calculateStats(trackItem.progress, trackItem.trackDetails);
        const levels = organizeByLevel(trackItem.progress, trackItem.trackDetails);
        const currentFocus = getCurrentFocus(trackItem.progress, trackItem.trackDetails);
        const hasProgress = trackItem.progress.length > 0;
        const canExpand = !!trackItem.trackDetails && levels.length > 0;

        return {
          ...trackItem,
          stats,
          levels,
          currentFocus,
          hasProgress,
          canExpand,
        };
      }),
    [tracks]
  );

  if (userLoading || isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (isError || !user) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-10">
        <div className="rounded-3xl border border-red-200 bg-red-50 p-8 text-center">
          <h1 className="mb-3 text-2xl font-bold text-red-800">User Not Found</h1>
          <p className="text-red-600">The selected user could not be loaded.</p>
        </div>
      </div>
    );
  }

  if (!hasLearnerRole) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-8">
        <Link
          to={`/users/${user.id}`}
          className="mb-6 inline-flex items-center gap-3 text-emerald-600 no-underline transition-colors hover:text-emerald-700 hover:no-underline"
        >
          <FaArrowLeft />
          Back to User Details
        </Link>

        <div className="rounded-3xl border border-amber-200 bg-amber-50 p-8">
          <h1 className="mb-2 text-2xl font-bold text-amber-900">Learner Progress Not Available</h1>
          <p className="text-amber-800">This user does not have a learner role.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 py-8">
      <div className="mx-auto max-w-7xl px-4">
        <Link
          to={`/users/${user.id}`}
          className="mb-6 inline-flex items-center gap-3 text-emerald-600 no-underline transition-colors hover:text-emerald-700 hover:no-underline"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
            <FaArrowLeft />
          </div>
          <span className="text-lg font-medium">Back to User Details</span>
        </Link>

        <div className="mb-8 rounded-3xl border border-gray-100 bg-white p-8 shadow-2xl">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="mb-2 text-4xl font-bold text-gray-900">Learner Track Progress</h1>
              <p className="text-lg text-gray-600">
                {user.name || user.email || user.id}
              </p>
            </div>
            <div className="rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-600 px-5 py-4 text-white shadow-lg">
              <div className="text-sm opacity-90">Assigned Tracks</div>
              <div className="text-3xl font-bold">{tracks.length}</div>
            </div>
          </div>
        </div>

        {tracks.length === 0 ? (
          <div className="rounded-3xl border border-gray-200 bg-white p-10 text-center shadow-xl">
            <FaBookOpen className="mx-auto mb-4 text-4xl text-gray-300" />
            <h2 className="mb-2 text-2xl font-semibold text-gray-800">No learner track progress found</h2>
            <p className="text-gray-600">This learner has no assigned learning tracks yet.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {processedTracks.map(({ track, progress, trackDetails, stats, levels, currentFocus, hasProgress, canExpand }) => {
              const isCompatible = track.organizationId === user?.organizationId;
              const shouldRenderTrack = isCompatible || showIncompatible[track.id];
              const isExpanded = expandedTrackIds[track.id] ?? false;

              if (!shouldRenderTrack) {
                return (
                  <section
                    key={track.id}
                    className="overflow-hidden rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-amber-100 p-8 shadow-xl"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-100 text-amber-600">
                        <FaExclamationTriangle className="text-xl" />
                      </div>
                      <div className="flex-1">
                        <h2 className="mb-2 text-xl font-bold text-amber-900">Learning Track Not Compatible</h2>
                        <p className="mb-5 text-sm leading-6 text-amber-800">
                          This learning track was assigned from a different organization. It is hidden by default to
                          match the behavior in the organization dashboard.
                        </p>
                        <button
                          type="button"
                          onClick={() =>
                            setShowIncompatible((prev) => ({
                              ...prev,
                              [track.id]: true,
                            }))
                          }
                          className="rounded-xl border border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-amber-800 transition-colors hover:bg-amber-50"
                        >
                          Show Learner Track Anyway
                        </button>
                      </div>
                    </div>
                  </section>
                );
              }

              return (
                <section key={track.id} className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-2xl">
                  <div className="border-b border-gray-100 bg-gradient-to-r from-slate-50 to-blue-50 p-8">
                    <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <div className="mb-3 flex flex-wrap items-center gap-2">
                          {track.langCode && (
                            <span className="rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700">
                              {(trackDetails?.langCode || track.langCode).toUpperCase()}
                            </span>
                          )}
                          {track.langLevel && (
                            <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                              {track.langLevel}
                            </span>
                          )}
                        </div>
                        <h2 className="mb-2 text-2xl font-bold text-gray-900">
                          {trackDetails?.title || `Track ${track.trackId}`}
                        </h2>
                        <div className="flex flex-wrap gap-5 text-sm text-gray-600">
                          <span>Assigned {formatDate(track.assignedAt)}</span>
                          {track.organizationId && <span>Space ID: {track.organizationId}</span>}
                          {track.projectId && <span>Project ID: {track.projectId}</span>}
                        </div>
                        </div>

                      {!trackDetails && (
                        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                          <div className="flex items-center gap-2 font-semibold">
                            <FaExclamationTriangle />
                            Track details could not be loaded
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="p-8">
                    {!hasProgress && (
                      <div className="mb-8 rounded-3xl border border-slate-200 bg-slate-50 p-6">
                        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                          <div>
                            <div className="mb-2 inline-flex rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-slate-600">
                              No Progress Yet
                            </div>
                            <h3 className="text-xl font-bold text-slate-900">This track has not been started</h3>
                            <p className="mt-1 text-sm text-slate-600">
                              The learner is assigned to this track, but there are no progress records yet.
                            </p>
                          </div>
                          <button
                            type="button"
                            disabled={!canExpand}
                            onClick={() =>
                              setExpandedTrackIds((prev) => ({
                                ...prev,
                                [track.id]: !isExpanded,
                              }))
                            }
                            className={`inline-flex items-center justify-center gap-3 rounded-2xl px-5 py-3 text-sm font-semibold shadow-lg transition-all duration-200 ${
                              !canExpand
                                ? "cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-400 shadow-none"
                                :
                              isExpanded
                                ? "border border-slate-300 bg-white text-slate-800 hover:bg-slate-100"
                                : "border border-sky-500 bg-gradient-to-r from-sky-500 via-cyan-500 to-blue-600 text-white shadow-sky-500/30 hover:-translate-y-0.5 hover:from-sky-600 hover:via-cyan-600 hover:to-blue-700"
                            }`}
                          >
                            <span
                              className={`flex h-8 w-8 items-center justify-center rounded-xl ${
                                !canExpand
                                  ? "bg-white text-slate-400"
                                  : isExpanded
                                    ? "bg-slate-100 text-slate-700"
                                    : "bg-white/20 text-white"
                              }`}
                            >
                              {!canExpand ? <FaChevronDown className="text-xs" /> : isExpanded ? <FaChevronUp className="text-xs" /> : <FaChevronDown className="text-xs" />}
                            </span>
                            <span className="text-left">
                              {!canExpand ? "No Track Details" : isExpanded ? "Hide Full Track" : "Open Full Track"}
                            </span>
                          </button>
                        </div>
                      </div>
                    )}

                    {hasProgress && currentFocus && (
                      <div className="mb-8 rounded-3xl border border-cyan-200 bg-gradient-to-r from-cyan-50 to-sky-50 p-6 shadow-sm">
                        <div className="mb-4 flex items-center justify-between gap-4">
                          <div>
                            <div className="mb-2 inline-flex rounded-full border border-cyan-200 bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-cyan-700">
                              Current Position
                            </div>
                            <h3 className="text-2xl font-bold text-slate-900">{currentFocus.topicTitle}</h3>
                            <p className="mt-1 text-sm text-slate-600">
                              {currentFocus.status === "COMPLETED" ? "Last updated topic" : "Current active topic"}
                            </p>
                          </div>
                          <button
                            type="button"
                            disabled={!canExpand}
                            onClick={() =>
                              setExpandedTrackIds((prev) => ({
                                ...prev,
                                [track.id]: !isExpanded,
                              }))
                            }
                            className={`inline-flex items-center justify-center gap-3 rounded-2xl px-5 py-3 text-sm font-semibold shadow-lg transition-all duration-200 ${
                              !canExpand
                                ? "cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-400 shadow-none"
                                :
                              isExpanded
                                ? "border border-slate-300 bg-white text-slate-800 hover:bg-slate-100"
                                : "border border-sky-500 bg-gradient-to-r from-sky-500 via-cyan-500 to-blue-600 text-white shadow-sky-500/30 hover:-translate-y-0.5 hover:from-sky-600 hover:via-cyan-600 hover:to-blue-700"
                            }`}
                          >
                            <span
                              className={`flex h-8 w-8 items-center justify-center rounded-xl ${
                                !canExpand
                                  ? "bg-white text-slate-400"
                                  : isExpanded
                                    ? "bg-slate-100 text-slate-700"
                                    : "bg-white/20 text-white"
                              }`}
                            >
                              {!canExpand ? <FaChevronDown className="text-xs" /> : isExpanded ? <FaChevronUp className="text-xs" /> : <FaChevronDown className="text-xs" />}
                            </span>
                            <span className="text-left">
                              {!canExpand ? "No Track Details" : isExpanded ? "Hide Full Track" : "Open Full Track"}
                            </span>
                          </button>
                        </div>

                        <div className="grid gap-4 md:grid-cols-4">
                          <div className="rounded-2xl bg-white/80 p-4">
                            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Level</div>
                            <div className="mt-2 text-lg font-bold text-slate-900">{currentFocus.level}</div>
                          </div>
                          <div className="rounded-2xl bg-white/80 p-4">
                            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Chapter</div>
                            <div className="mt-2 text-lg font-bold text-slate-900">{currentFocus.chapterTitle}</div>
                          </div>
                          <div className="rounded-2xl bg-white/80 p-4">
                            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Component</div>
                            <div className="mt-2 text-lg font-bold text-slate-900">{currentFocus.currentComponentOrder || 0}</div>
                          </div>
                          <div className="rounded-2xl bg-white/80 p-4">
                            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Time Spent</div>
                            <div className="mt-2 text-lg font-bold text-slate-900">{formatMinutes(currentFocus.spentTimeInMinutes)}</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {hasProgress && (
                      <div className="mb-8 grid gap-4 md:grid-cols-3">
                      <div className="rounded-2xl border border-blue-100 bg-blue-50 p-5">
                        <div className="mb-2 flex items-center gap-3 text-blue-700">
                          <FaCheckCircle />
                          <span className="text-sm font-medium">Completed Topics</span>
                        </div>
                        <div className="text-3xl font-bold text-blue-900">
                          {stats.completed}
                          <span className="ml-2 text-base font-medium text-blue-700">/ {stats.total}</span>
                        </div>
                      </div>

                      <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-5">
                        <div className="mb-2 flex items-center gap-3 text-emerald-700">
                          <FaLayerGroup />
                          <span className="text-sm font-medium">Overall Progress</span>
                        </div>
                        <div className="text-3xl font-bold text-emerald-900">{stats.percentage}%</div>
                      </div>

                      <div className="rounded-2xl border border-amber-100 bg-amber-50 p-5">
                        <div className="mb-2 flex items-center gap-3 text-amber-700">
                          <FaClock />
                          <span className="text-sm font-medium">Time Spent</span>
                        </div>
                        <div className="text-3xl font-bold text-amber-900">{formatMinutes(stats.timeSpent)}</div>
                      </div>
                      </div>
                    )}

                    {hasProgress && (
                      <div className="mb-8">
                        <div className="mb-2 flex items-center justify-between text-sm font-medium text-gray-700">
                          <span>Track completion</span>
                          <span>{stats.percentage}%</span>
                        </div>
                        <div className="h-3 overflow-hidden rounded-full bg-gray-100">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-500"
                            style={{ width: `${stats.percentage}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {(hasProgress || isExpanded) && levels.length > 0 ? (
                      <div className="space-y-6">
                        {(isExpanded || !hasProgress || !currentFocus
                          ? levels
                          : levels.filter((level) => level.level === currentFocus.level)
                        ).map((level) => (
                          <div key={`${track.id}-${level.level}`} className="rounded-2xl border border-gray-200 bg-slate-50 p-5">
                            <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                              <div>
                                <h3 className="text-xl font-semibold text-gray-900">{level.level}</h3>
                                <p className="text-sm text-gray-600">
                                  {level.stats.completed} of {level.stats.total} topics completed
                                </p>
                              </div>
                              <div className="min-w-[220px]">
                                <div className="mb-1 flex items-center justify-between text-xs font-medium text-gray-600">
                                  <span>Level progress</span>
                                  <span>{level.stats.percentage}%</span>
                                </div>
                                <div className="h-2 overflow-hidden rounded-full bg-gray-200">
                                  <div
                                    className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-600"
                                    style={{ width: `${level.stats.percentage}%` }}
                                  />
                                </div>
                              </div>
                            </div>

                            <div className="space-y-4">
                              {(isExpanded || !hasProgress || !currentFocus
                                ? level.chapters
                                : level.chapters.filter((chapter) => chapter.chapter.id === currentFocus.chapterId)
                              ).map((chapter) => (
                                <div key={chapter.chapter.id} className="rounded-2xl border border-white bg-white p-4 shadow-sm">
                                  <div className="mb-3 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                                    <div>
                                      <h4 className="font-semibold text-gray-900">{chapter.chapter.title}</h4>
                                      <p className="text-sm text-gray-600">
                                        {chapter.stats.completed} of {chapter.stats.total} topics completed
                                      </p>
                                    </div>
                                    <div className="text-sm font-medium text-gray-700">
                                      {chapter.stats.percentage}% • {formatMinutes(chapter.stats.timeSpent)}
                                    </div>
                                  </div>

                                  <div className="mb-4 h-2 overflow-hidden rounded-full bg-gray-100">
                                    <div
                                      className="h-full rounded-full bg-gradient-to-r from-sky-500 to-blue-600"
                                      style={{ width: `${chapter.stats.percentage}%` }}
                                    />
                                  </div>

                                  <div className="space-y-2">
                                    {(isExpanded || !hasProgress || !currentFocus
                                      ? chapter.topics
                                      : chapter.topics.filter(({ topic }) => topic.id === currentFocus.topicId)
                                    ).map(({ topic, progress: topicProgress }) => (
                                      <div
                                        key={topic.id}
                                        className={`flex flex-col gap-2 rounded-xl border px-4 py-3 md:flex-row md:items-center md:justify-between ${
                                          getTopicStatus(topicProgress) === "COMPLETED"
                                            ? "border-emerald-200 bg-emerald-50"
                                            : getTopicStatus(topicProgress) === "SKIPPED"
                                              ? "border-slate-200 bg-slate-50"
                                              : "border-gray-200 bg-gray-50"
                                        }`}
                                      >
                                        <div className="flex items-start gap-3">
                                          <div
                                            className={`mt-0.5 flex h-6 w-6 items-center justify-center rounded-full text-xs ${
                                              getTopicStatus(topicProgress) === "COMPLETED"
                                                ? "bg-emerald-500 text-white"
                                                : getTopicStatus(topicProgress) === "SKIPPED"
                                                  ? "bg-slate-400 text-white"
                                                  : "bg-gray-300 text-gray-700"
                                            }`}
                                          >
                                            {topic.order}
                                          </div>
                                          <div>
                                            <div className="font-medium text-gray-900">{topic.title}</div>
                                            <div className="text-xs text-gray-500">
                                              {getTopicStatus(topicProgress) === "COMPLETED"
                                                ? "Completed"
                                                : getTopicStatus(topicProgress) === "SKIPPED"
                                                  ? "Skipped"
                                                  : "In progress"}
                                            </div>
                                          </div>
                                        </div>
                                        <div className="text-sm text-gray-600">
                                          {formatMinutes(getTopicTimeSpent(topicProgress))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : hasProgress ? (
                      <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6 text-gray-600">
                        No chapter details are available for this track.
                      </div>
                    ) : null}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
